// src/features/auth/Register.js
import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API from '../../api/axiosConfig';

function Register() {
  const [formData, setFormData] = useState({
    first_name: '', 
    last_name: '',  
    username: '',
    email: '', 
    password: '',
    class_level: 5
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false); 
  const [isLoading, setIsLoading] = useState(false);
  const [userType, setUserType] = useState('student');
  const navigate = useNavigate();

  // СОСТОЯНИЯ ДЛЯ ВСПЛЫВАЮЩЕГО УВЕДОМЛЕНИЯ О ЯЗЫКЕ
  const [langWarning, setLangWarning] = useState('');
  const warningTimeout = useRef(null);

  // ФУНКЦИЯ ПОКАЗА УВЕДОМЛЕНИЯ
  const showLangWarning = (message) => {
    setLangWarning(message);
    if (warningTimeout.current) {
      clearTimeout(warningTimeout.current);
    }
    warningTimeout.current = setTimeout(() => {
      setLangWarning('');
    }, 3000); // Уведомление исчезнет через 3 секунды
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await API.post('/api/register/', {
        ...formData,
        role: userType 
      });
      
      setSuccess(true);
      setTimeout(() => navigate('/login'), 6000);
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        if (data.error) {
          setError(data.error);
        } else if (typeof data === 'object') {
          const errorMessages = Object.entries(data)
            .map(([field, msgs]) => {
              const fieldName = 
                field === 'username' ? 'Логин' : 
                field === 'password' ? 'Пароль' : 
                field === 'email' ? 'Email' : 
                field === 'first_name' ? 'Имя' : 
                field === 'last_name' ? 'Фамилия' : field;
              return `${fieldName}: ${Array.isArray(msgs) ? msgs.join(' ') : msgs}`;
            })
            .join('\n');
            
          setError(errorMessages || 'Ошибка при регистрации. Проверьте данные.');
        }
      } else {
        setError('Нет связи с сервером. Проверьте подключение к интернету.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ОБНОВЛЕННАЯ ФИЛЬТРАЦИЯ ДЛЯ ЛОГИНА
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    if (/^[a-zA-Z0-9_@.+\-]*$/.test(value)) {
      setFormData({ ...formData, username: value });
      setLangWarning(''); // Прячем ошибку, если ввод правильный
    } else {
      showLangWarning('Пожалуйста, используйте только английские буквы для логина!');
    }
  };

  // ОБНОВЛЕННАЯ ФИЛЬТРАЦИЯ ДЛЯ ПАРОЛЯ
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    if (/^[\x21-\x7E]*$/.test(value)) {
      setFormData({ ...formData, password: value });
      setLangWarning(''); // Прячем ошибку, если ввод правильный
    } else {
      showLangWarning('Пароль может содержать только английские буквы, цифры и спецсимволы!');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center px-4 py-12 relative overflow-hidden">
      
      {/* ВСПЛЫВАЮЩЕЕ УВЕДОМЛЕНИЕ О ЯЗЫКЕ (TOAST) */}
      <div 
        className={`fixed top-6 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-2xl shadow-2xl z-50 flex items-center gap-3 transition-all duration-300 ${
          langWarning ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10 pointer-events-none'
        }`}
      >
        <span className="text-xl">⚠️</span>
        <span className="font-bold text-sm">{langWarning}</span>
      </div>

      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden z-10">
        <div className="p-8 md:p-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 text-purple-600 rounded-2xl mb-4">
              <span className="text-3xl">📝</span>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">Создать аккаунт</h2>
            <p className="text-gray-500 mt-2">Присоединяйтесь к нашей платформе</p>
          </div>

          {success ? (
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-xl text-center">
              <h3 className="text-green-800 font-bold text-lg mb-2">Письмо отправлено!</h3>
              <p className="text-green-700">
                Мы отправили ссылку для активации на <strong>{formData.email}</strong>. 
                Пожалуйста, подтвердите почту, чтобы войти в аккаунт.
              </p>
              <div className="mt-4 animate-pulse text-sm text-green-600">
                Перенаправление на страницу входа...
              </div>
            </div>
          ) : (
            <form onSubmit={handleRegister} className="space-y-5">
              
              {/* УВЕДОМЛЕНИЕ ОБ ОШИБКЕ СЕРВЕРА */}
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl shadow-sm">
                  <div className="flex items-center gap-2 text-red-800 font-bold mb-1 text-sm">
                    <span>⚠️</span> Ошибка регистрации
                  </div>
                  <p className="text-sm text-red-700 whitespace-pre-line font-medium leading-relaxed">
                    {error}
                  </p>
                </div>
              )}

              {/* Выбор типа пользователя */}
              <div>
                <label className="block text-xs font-black uppercase text-gray-400 mb-2">Вы регистрируетесь как:</label>
                <div className="flex gap-2 p-1 bg-gray-100 rounded-2xl">
                  <button 
                    type="button"
                    onClick={() => setUserType('student')}
                    className={`flex-1 py-3 rounded-xl font-bold transition-all ${userType === 'student' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400'}`}
                  >
                    Учащийся
                  </button>
                  <button 
                    type="button"
                    onClick={() => setUserType('teacher')}
                    className={`flex-1 py-3 rounded-xl font-bold transition-all ${userType === 'teacher' ? 'bg-white shadow-sm text-purple-600' : 'text-gray-400'}`}
                  >
                    Учитель
                  </button>
                </div>
              </div>

              {/* ИМЯ И ФАМИЛИЯ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Имя</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 outline-none transition-all"
                    placeholder="Напр. Иван"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Фамилия</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 outline-none transition-all"
                    placeholder="Напр. Иванов"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  />
                </div>
              </div>

              {/* ЛОГИН */}
              <div>
                <div className="flex justify-between items-end mb-1">
                  <label className="block text-sm font-semibold text-gray-700">Логин</label>
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Только English</span>
                </div>
                <input
                  type="text"
                  required
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-4 outline-none transition-all ${
                    langWarning.includes('логина') ? 'border-red-500 focus:ring-red-500/20 bg-red-50' : 'border-gray-200 focus:border-purple-500 focus:ring-purple-500/10'
                  }`}
                  placeholder="Придумайте логин"
                  value={formData.username}
                  onChange={handleUsernameChange}
                />
              </div>

              {/* EMAIL */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Электронная почта</label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 outline-none transition-all"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

              {/* КЛАСС */}
              {userType === 'student' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Ваш класс</label>
                  <select 
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-blue-500 outline-none transition-all"
                    value={formData.class_level}
                    onChange={(e) => setFormData({...formData, class_level: e.target.value})}
                  >
                    {[5, 6, 7, 8, 9, 10, 11].map(num => (
                      <option key={num} value={num}>{num} класс</option>
                    ))}
                  </select>
                </div>
              )}

              {/* ПАРОЛЬ */}
              <div>
                <div className="flex justify-between items-end mb-1">
                  <label className="block text-sm font-semibold text-gray-700">Пароль</label>
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Только English</span>
                </div>
                <input
                  type="password"
                  required
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-4 outline-none transition-all ${
                    langWarning.includes('Пароль') ? 'border-red-500 focus:ring-red-500/20 bg-red-50' : 'border-gray-200 focus:border-purple-500 focus:ring-purple-500/10'
                  }`}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handlePasswordChange}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-4 rounded-xl font-bold text-white shadow-lg transform transition-all active:scale-95 ${
                  isLoading ? 'bg-gray-400' : userType === 'teacher' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isLoading ? 'Обработка...' : 'Зарегистрироваться'}
              </button>
            </form>
          )}

          <div className="mt-8 pt-8 border-t border-gray-100 text-center">
            <p className="text-gray-600">
              Уже есть аккаунт?{' '}
              <Link to="/login" className="text-purple-600 font-bold hover:underline">
                Войти
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;